import { HttpInterceptorFn } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * On the server side:
 * 1) Rewrite asset requests that Angular SSR resolved to the public host
 *    back to localhost so they're served locally (avoids malformed URLs
 *    like http://host:443 and unnecessary outbound requests).
 * 2) Block outbound requests to unknown origins (SSRF prevention).
 */
export const ssrUrlGuardInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformServer(platformId)) {
    return next(req);
  }

  // Allow relative URLs (assets, i18n, etc.)
  if (!req.url.startsWith('http://') && !req.url.startsWith('https://')) {
    return next(req);
  }

  // Rewrite absolute asset/i18n URLs to local server
  // SSR resolves "/assets/..." to "http://publichost:port/assets/..." which is wrong
  const ssrPort = process.env['PORT'] || '4100';
  try {
    const parsed = new URL(req.url);
    if (parsed.pathname.startsWith('/assets/') || parsed.pathname.startsWith('/favicon')) {
      const localUrl = `http://127.0.0.1:${ssrPort}${parsed.pathname}${parsed.search}`;
      return next(req.clone({ url: localUrl }));
    }
  } catch {
    // If URL parsing fails, fall through to the guard
  }

  // Allow requests to the configured API base URL
  const apiBase = environment.API_BASE_URL;
  if (apiBase && req.url.startsWith(apiBase)) {
    return next(req);
  }

  // Allow Google reCAPTCHA
  if (req.url.includes('google.com/recaptcha')) {
    return next(req);
  }

  // Block all other absolute URLs during SSR
  console.warn(`[SSR] Blocked outbound request to: ${req.url}`);
  throw new Error(`SSR request blocked: ${req.url} is not an allowed origin`);
};
